import React from 'react';
import { Check, ArrowRight } from 'lucide-react';

// "Keep what you have, or step down" (Erik, Oct 26 2026). Trial users get BOTH books for 14 days;
// the old upgrade buttons sold Preserver alone, silently dropping Maximizer. This card makes the
// choice explicit and leads with continuity: what you have now (both books) vs Preserver only.
//   mode: 'trial' (still has full access) | 'free' (access has ended)
//   onChoose(plan, maximizer)
export default function PlanChoiceCard({ mode = 'trial', pricing: pricingProp, onChoose, loading = false, compact = false }) {
  const [fetched, setFetched] = React.useState(null);
  React.useEffect(() => {
    if (pricingProp) return;
    const base = import.meta.env.VITE_API_URL || '';
    fetch(`${base}/api/billing/founding-status`).then(r => r.json()).then(d => setFetched(d?.pricing || null)).catch(() => {});
  }, [pricingProp]);
  const pricing = pricingProp || fetched;
  const intro = pricing?.intro_active !== false;
  const base = pricing?.intro_monthly ?? (intro ? 59 : 129);
  const addon = pricing?.maximizer_addon_monthly ?? (intro ? 79 : 100);
  const both = base + addon;
  const lock = pricing?.intro_lock_months ?? 12;
  const trial = mode === 'trial';

  const Bullet = ({ children, muted }) => (
    <li className={`flex items-start gap-2 text-sm ${muted ? 'text-ink-light' : 'text-ink'}`}>
      <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${muted ? 'text-ink-light' : 'text-positive'}`} />
      <span>{children}</span>
    </li>
  );

  return (
    <div className="text-left">
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? 'gap-3' : 'gap-4'}`}>
        {/* LEFT — continuity (primary) */}
        <div className="rounded-[2px] border-2 border-ink bg-paper-card p-4 flex flex-col">
          <div className="font-mono text-[0.62rem] tracking-[0.18em] uppercase text-claret mb-1">
            {trial ? 'What you have now' : 'Everything you had in your trial'}
          </div>
          <div className="font-display text-[1.2rem] text-ink leading-tight" style={{ fontVariationSettings: '"opsz" 48' }}>
            Preserver + Maximizer
          </div>
          <div className="mt-1 mb-3">
            <span className="font-display text-[1.6rem] text-ink">${both}</span>
            <span className="text-sm text-ink-mute">/mo{intro ? ` intro · locked ${lock} mo` : ''}</span>
            <div className="text-xs text-ink-light mt-0.5">${base} Preserver + ${addon} Maximizer</div>
          </div>
          <ul className="space-y-1.5 mb-4 flex-1">
            <Bullet>Both books — the base and the breakout book</Bullet>
            <Bullet>Live signals and plain sell alerts</Bullet>
            <Bullet>Volatility brake and rebalance instructions</Bullet>
          </ul>
          <button
            onClick={() => onChoose('monthly', true)}
            disabled={loading}
            className="w-full px-4 py-2.5 bg-ink text-paper font-medium rounded-[2px] hover:bg-claret transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? 'Loading…' : <>{trial ? 'Continue' : 'Get both back'} — ${both}/mo <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>

        {/* RIGHT — step down */}
        <div className="rounded-[2px] border border-rule-dark bg-paper p-4 flex flex-col">
          <div className="font-mono text-[0.62rem] tracking-[0.18em] uppercase text-ink-mute mb-1">
            Or step down
          </div>
          <div className="font-display text-[1.2rem] text-ink leading-tight" style={{ fontVariationSettings: '"opsz" 48' }}>
            Preserver only
          </div>
          <div className="mt-1 mb-3">
            <span className="font-display text-[1.6rem] text-ink">${base}</span>
            <span className="text-sm text-ink-mute">/mo{intro ? ` intro · locked ${lock} mo` : ''}</span>
          </div>
          <ul className="space-y-1.5 mb-4 flex-1">
            <Bullet>The base book — built so you never sell at the bottom</Bullet>
            <Bullet>Live signals and plain sell alerts</Bullet>
            <Bullet muted>No breakout book (add Maximizer anytime)</Bullet>
          </ul>
          <button
            onClick={() => onChoose('monthly', false)}
            disabled={loading}
            className="w-full px-4 py-2.5 border border-ink text-ink font-medium rounded-[2px] hover:bg-paper-deep transition-colors disabled:opacity-50"
          >
            Switch to Preserver — ${base}/mo
          </button>
        </div>
      </div>
      <p className="text-xs text-ink-mute mt-3 text-center">
        30-day money-back guarantee · cancel anytime ·{' '}
        <button onClick={() => onChoose('annual', true)} className="underline hover:text-ink">prefer yearly billing?</button>
      </p>
    </div>
  );
}
